import LocationInterface from '@solvhealth/types/interfaces/Location';
import { GenericObj } from '@solvhealth/types/interfaces/generics';
import { Box, Icons, Stack, Text } from '@solvhealth/jigsaw-v1';
// @ts-ignore FIXME: Try `npm install @types/rc-form` if it exists or a... Remove this comment to see the full error message
import { createForm, formShape } from 'rc-form';
import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  PAPERWORK_PAYMENT_SELECT_USE_SAVED_INSURANCE,
  PAPERWORK_PAYMENT_SELECT_ADD_NEW_INSURANCE,
} from '~/core/analytics/events';
import { actions } from '~/ducks/insuranceCarrierPlans';
import { deleteNullFields, sanitizeForm } from '../../../../core/util/form';
import PaperworkForm from '../../PaperworkForm';
import { setLoginAction } from '~/actions/login';
import { showModal } from '~/actions/runtime';
import { PaymentPageNumber } from './constants';
import { SectionPage } from '~/components/Paperwork/interfaces';
import {
  processFormValuesForAutocompleteAnswers,
  useShouldPrimaryInsuranceCarrierAutocompleteBeAdministered,
} from '~/components/Paperwork/components/Payment/util';
import { InsuranceCarrierAutocomplete } from '~/components/Paperwork/components/Payment/InsuranceAutocompletes';
import {
  PaperworkFieldName,
  PAPERWORK_FIELD_ANSWER_YES,
} from '~/components/Paperwork/paperworkConstants';
import { LOGIN_ACTION_LOGIN } from '~/constants';
import { LOGIN_SIGN_UP_MODAL } from '~/components/LoginSignUpModal';
import { isInGroup, isPartOfMulticareGroup } from '~/core/util/location';
import MobileAddInsurance from '~/components/AddInsurance/MobileAddInsurance';
import { insurersError, insurersReceived } from '~/actions/insurers';
import { apiGetDispatchable } from '~/core/dapi';
import { getInsurersListUrl } from '~/core/dapi/insurers';
import PaperworkFields from '../../util/PaperworkFields';
import {
  useCopyInsuranceToggle,
  usePreloadedData,
  InsuranceProfile,
  useAutoPopulateGuarantorFields,
} from '../../hooks';
import { validateFamilyBooking } from '~/util/validateFamilyBooking';
import { usePaperworkContextBookingId } from '~/routes/paperwork/PaperworkBookingIdContext';
import { useSolvSelector } from '~/reducers';
import AuthenticationContext from '~/core/login/AuthenticationContext';
import InsuranceUploadOptionCards from '~/components/Paperwork/components/Payment/InsuranceUploadOptionCards';
import ExistingInsuranceProfiles from '~/components/Paperwork/components/Payment/ExistingInsuranceProfiles';
import PaymentSectionSubheader from '~/components/Paperwork/components/Payment/PaymentSectionSubheader';
import { usePalette } from '~/hooks/usePalette';
import { useGeneralConfig } from '~/core/dapi/generalConfig';
import { MEDSTAR } from '~/config/locations';

const fieldNameIsInsuranceAutocompleteField = (fieldName: string) =>
  fieldName === PaperworkFieldName.PRIMARY_INSURANCE_COMPANY ||
  fieldName === PaperworkFieldName.PRIMARY_INSURANCE_PLAN_NAME;

type Props = {
  form: formShape;
  onGoForward: () => void;
  handleClickNext: (newForm: GenericObj, extraFields: GenericObj) => void;
  sectionPage: SectionPage;
  addInsurance?: any;
  clearData?: (...args: any[]) => any;
  fetchInsurersList?: (...args: any[]) => any;
  handleFileChange: (...args: any[]) => any;
  insuranceCard?: any;
  insuranceCoverage?: any;
  insurers?: any[];
  submitInsuranceInformation?: (...args: any[]) => any;
  searchPreferences: any;
};

export type InsuranceUploadOption = {
  icon: ReactNode;
  label: string;
  value: any;
  subtitle?: string;
};
export type InsuranceUploadOptions = 'selectExisting' | 'addNewInsurance';

export const SELECT_EXISTING = 'selectExisting';
export const ADD_NEW_INSURANCE = 'addNewInsurance';

const PaymentInfoForm = (props: Props) => {
  const {
    form,
    handleClickNext,
    onGoForward,
    sectionPage,
    addInsurance,
    clearData,
    fetchInsurersList,
    handleFileChange,
    insuranceCard,
    insuranceCoverage,
    insurers,
    submitInsuranceInformation,
    searchPreferences,
  } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation(['paperwork', 'common']);
  const [showUploadInsurance, setShowUploadInsurance] = useState(false);
  const [insuranceUploadMethod, setInsuranceUploadMethod] =
    useState<InsuranceUploadOptions>(SELECT_EXISTING);
  const [isClearingData, setIsClearingData] = useState(false);

  const location = useSolvSelector((state) => state.location as LocationInterface);
  const paperwork = useSolvSelector((state) => state.paperwork);
  const preloadedData = usePreloadedData();
  const bookingId = usePaperworkContextBookingId();
  const booking = useSolvSelector((state) => state.pastBookings[bookingId] || {});
  const { isLoggedIn } = useContext(AuthenticationContext);
  const { insuranceProfiles } = preloadedData;
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceProfile>();

  const formRef = useRef(form);
  const { insurance } = addInsurance;
  const selfPayCheck = paperwork.formData[PaperworkFieldName.SELF_PAY_CHECK];

  const isInsuranceInfoPage = paperwork.pageView === PaymentPageNumber.InsuranceInfo;
  const isSelectExistingScreen = insuranceUploadMethod === SELECT_EXISTING;
  const isMulticare = isPartOfMulticareGroup(location);
  const shouldShowSelfPayCheck = paperwork.pageView === PaymentPageNumber.SelfPayCheck;
  const shouldShowUploadOptions = !isMulticare && isInsuranceInfoPage && isSelectExistingScreen;
  const shouldShowExistingInsurance = isSelectExistingScreen && isLoggedIn && isInsuranceInfoPage;
  const shouldShowUploadInsurance =
    (insuranceUploadMethod === ADD_NEW_INSURANCE || isMulticare) &&
    [PaymentPageNumber.InsuranceInfo, PaymentPageNumber.SecondaryInsurance].includes(
      paperwork.pageView
    );
  const insuranceCarrierAndPlanAutocompleteEnabled =
    useShouldPrimaryInsuranceCarrierAutocompleteBeAdministered(paperwork);
  const shouldShowDefaultFields =
    paperwork.pageView === PaymentPageNumber.SecondaryInsurance ||
    paperwork.pageView === PaymentPageNumber.GuarantorInfo;
  const insuranceUploadOptions: InsuranceUploadOption[] = [];
  const shouldDisableNextButton =
    isSelectExistingScreen && isInsuranceInfoPage && !selectedInsurance;
  const { data: ocrEnabled } = useGeneralConfig(
    'enable_insurance_ocr_paperwork',
    location.id,
    true,
    false
  );

  if (showUploadInsurance && isInsuranceInfoPage) {
    if (!isLoggedIn) {
      insuranceUploadOptions.push({
        icon: <Icons.Avatar />,
        label: t('paperwork:payment.selectExisting'),
        subtitle: t(`paperwork:payment.${SELECT_EXISTING}Subtitle`) as string,
        value: SELECT_EXISTING,
      });
    }
    insuranceUploadOptions.push({
      icon: <Icons.InsuranceCard />,
      label: t('paperwork:payment.addNewInsurance'),
      value: ADD_NEW_INSURANCE,
    });
  }

  useAutoPopulateGuarantorFields(formRef, paperwork);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (isPartOfMulticareGroup(location)) {
      setInsuranceUploadMethod(ADD_NEW_INSURANCE);
    } else if (paperwork.pageView === PaymentPageNumber.SelfPayCheck) {
      setInsuranceUploadMethod(SELECT_EXISTING);
    }
  }, [paperwork.pageView, location]);

  useEffect(() => {
    if (!isClearingData) {
      setIsClearingData(true);

      if (clearData) clearData();
    }
  }, [clearData, isClearingData, paperwork.formData, selfPayCheck]);

  useEffect(() => {
    const isScanStarted = () => addInsurance?.front.scanStarted || addInsurance?.back.scanStarted;

    if (isScanStarted() && !isEmpty(insurance)) {
      const updateFields: { [key in PaperworkFieldName]?: string } = {};
      if (insurance.firstName)
        updateFields[PaperworkFieldName.PRIMARY_POLICY_HOLDER_FIRST_NAME] = insurance.firstName;
      if (insurance.lastName)
        updateFields[PaperworkFieldName.PRIMARY_POLICY_HOLDER_LAST_NAME] = insurance.lastName;
      if (insurance.memberId)
        updateFields[PaperworkFieldName.PRIMARY_MEMBER_ID] = insurance.memberId;
      if (insurance.insuranceCarrier)
        updateFields[PaperworkFieldName.PRIMARY_INSURANCE_COMPANY] = insurance.insuranceCarrier;

      if (!isEmpty(updateFields)) formRef.current.setFieldsValue(updateFields);
    }
  }, [addInsurance, insurance]);

  useEffect(() => {
    setShowUploadInsurance(selfPayCheck === PAPERWORK_FIELD_ANSWER_YES);
  }, [selfPayCheck]);

  const searchStateCode = searchPreferences?.location?.searchStateCode ?? null;
  useEffect(() => {
    // don't search if we have the data in store
    if (isEmpty(insurers)) {
      dispatch(
        apiGetDispatchable(
          getInsurersListUrl(searchStateCode, isInGroup(location, MEDSTAR) ? MEDSTAR : ''),
          insurersReceived,
          insurersError
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insurers, searchStateCode]);

  useEffect(() => {
    if (insuranceCarrierAndPlanAutocompleteEnabled) {
      dispatch(actions.fetchInsuranceCarrierPlansForLocation({ locationId: location.id }));
    }
  }, [dispatch, location.id, insuranceCarrierAndPlanAutocompleteEnabled]);

  const handleNextButtonClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    form.validateFields((errors: any, values: any) => {
      if (!errors && !isEmpty(values)) {
        const newForm = sanitizeForm(deleteNullFields(values));
        const [formValuesWithStrAutocompleteFields, autocompleteOptions] =
          insuranceCarrierAndPlanAutocompleteEnabled
            ? processFormValuesForAutocompleteAnswers(newForm)
            : [newForm, {}];
        const cardFront = insurance?.cardIds?.front;
        const cardBack = insurance?.cardIds?.back;
        let extraFields = {};
        if (cardFront) {
          extraFields = {
            ...extraFields,
            cardFront,
          };
        }
        if (cardBack) {
          extraFields = {
            ...extraFields,
            cardBack,
          };
        }
        if (selectedInsurance?.insurance_profile_id) {
          extraFields = {
            ...extraFields,
            insuranceProfileId: selectedInsurance.insurance_profile_id,
          };
        }
        handleClickNext(formValuesWithStrAutocompleteFields, extraFields);
      }

      return null;
    });
  };

  const handleInsuranceProfileSelect = (insuranceProfileId?: string) => {
    const selectedProfile = insuranceProfiles?.find((ip) => {
      return ip.insurance_profile_id === insuranceProfileId;
    });
    setSelectedInsurance(selectedProfile);
  };
  const { paletteName } = usePalette();

  const handleInsuranceUploadMethodSelection = (selectedOptionName: InsuranceUploadOptions) => {
    if (selectedOptionName === SELECT_EXISTING) {
      analyticsTrackEvent(PAPERWORK_PAYMENT_SELECT_USE_SAVED_INSURANCE, { isLoggedIn });
      if (!isLoggedIn) {
        dispatch(setLoginAction(LOGIN_ACTION_LOGIN));
        dispatch(showModal(LOGIN_SIGN_UP_MODAL));
      }
    }
    if (selectedOptionName === ADD_NEW_INSURANCE) {
      analyticsTrackEvent(PAPERWORK_PAYMENT_SELECT_ADD_NEW_INSURANCE, { isLoggedIn });
      if (selectedInsurance) {
        handleInsuranceProfileSelect(undefined);
      }
    }
    setInsuranceUploadMethod(selectedOptionName);
  };

  const initialCarrierOptionLabelForAutocomplete = selectedInsurance?.insurance_profile_id ?? null;
  const { isFamilyBooking } = validateFamilyBooking(booking);
  const { toggleInsurance, hasInsuranceToCopy, checkboxMeta } = useCopyInsuranceToggle(bookingId);
  const preloadedDataWithInsurance = {
    ...preloadedData,
    selectedInsurance,
  };

  const selfPayCheckOptions = () => {
    if (shouldShowSelfPayCheck) {
      return (
        <PaperworkFields
          fieldNames={sectionPage.fieldNames}
          form={form}
          preloadedData={preloadedData}
        />
      );
    }
    return null;
  };

  const chooseInsuranceMethodOptions = () => {
    if (shouldShowUploadOptions) {
      return (
        <InsuranceUploadOptionCards
          handleInsuranceUploadMethodSelection={handleInsuranceUploadMethodSelection}
          insuranceUploadOptions={insuranceUploadOptions}
          shouldShowExistingInsurance={shouldShowExistingInsurance && !isEmpty(insuranceProfiles)}
        />
      );
    }
    return null;
  };

  const carrierAndPlanAutocomplete = () => {
    return (
      <InsuranceCarrierAutocomplete
        form={form}
        initialCarrierOptionLabel={initialCarrierOptionLabelForAutocomplete}
        paperwork={paperwork}
      />
    );
  };

  const manualFields = () => {
    return (
      <>
        {insuranceCarrierAndPlanAutocompleteEnabled && carrierAndPlanAutocomplete()}
        <PaperworkFields
          checkboxEnabled={shouldShowUploadInsurance ? hasInsuranceToCopy : false}
          checkboxMeta={checkboxMeta}
          fieldNames={sectionPage.fieldNames?.filter(
            (name) =>
              PaperworkFieldName.SELF_PAY_CHECK !== name &&
              // If the insurance carrier/plan autocomplete is enabled, we don't
              // want to administer the regular input fields for those PFs
              !(
                insuranceCarrierAndPlanAutocompleteEnabled &&
                fieldNameIsInsuranceAutocompleteField(name)
              )
          )}
          form={form}
          preloadedData={preloadedDataWithInsurance}
          toggleSideEffect={
            shouldShowUploadInsurance && hasInsuranceToCopy && isFamilyBooking
              ? toggleInsurance
              : undefined
          }
        />
      </>
    );
  };

  /** When selecting existing insurance, only show manual inputs if an insurance plan has been selected **/
  const selectInsuranceConfirmationInputs = () => {
    if (selectedInsurance) {
      return manualFields();
    }
    return (
      <Box
        sx={{
          height: 0,
          overflow: 'hidden',
        }}
      >
        {manualFields()}
      </Box>
    );
  };

  const existingInsuranceOptions = () => {
    if (shouldShowExistingInsurance) {
      return (
        <ExistingInsuranceProfiles
          handleInsuranceProfileSelect={handleInsuranceProfileSelect}
          insuranceProfiles={insuranceProfiles}
          manualInsuranceInputs={selectInsuranceConfirmationInputs()}
          selectedInsurance={selectedInsurance}
        />
      );
    }
    return null;
  };

  const uploadCardOption = () => {
    if (shouldShowUploadInsurance) {
      return (
        <>
          {ocrEnabled && (
            <div>
              <Stack row space={1}>
                <Text variant="subtitle">{t('paperwork:payment.photoUpload')}</Text>
                <Text color="gray-500" variant="body">
                  {t('paperwork:payment.optional')}
                </Text>
              </Stack>
              <MobileAddInsurance
                // @ts-ignore
                addInsurance={addInsurance}
                booking={booking}
                clearData={clearData}
                fetchInsurersList={fetchInsurersList}
                handleEnterManually={() => setShowUploadInsurance(false)}
                handleFileChange={handleFileChange}
                hideCta
                insuranceCard={insuranceCard}
                insuranceCoverage={insuranceCoverage}
                insurers={insurers}
                location={location}
                submitInsuranceInformation={submitInsuranceInformation}
                theme={paletteName}
              />
            </div>
          )}
          {manualFields()}
        </>
      );
    }
    return null;
  };

  const defaultFields = () => {
    if (shouldShowDefaultFields) {
      return (
        <PaperworkFields
          fieldNames={sectionPage.fieldNames}
          form={form}
          preloadedData={preloadedData}
        />
      );
    }
    return null;
  };

  const statefulSectionPage = shouldDisableNextButton
    ? { ...sectionPage, allowDisableSubmit: true }
    : sectionPage;

  return (
    <PaperworkForm
      form={form}
      onSkip={onGoForward}
      onSubmit={handleNextButtonClick}
      sectionPage={statefulSectionPage}
    >
      <PaymentSectionSubheader
        hasInsuranceProfiles={!isEmpty(insuranceProfiles)}
        shouldShowExistingInsurance={shouldShowExistingInsurance}
        shouldShowUploadInsurance={shouldShowUploadInsurance}
        shouldShowUploadOptions={shouldShowUploadOptions}
      />
      {existingInsuranceOptions()}
      {uploadCardOption()}
      {chooseInsuranceMethodOptions()}
      {selfPayCheckOptions()}
      {defaultFields()}
    </PaperworkForm>
  );
};

export default createForm()(PaymentInfoForm);
